import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SEO } from "@/components/SEO";
import { toast } from "sonner";
import { ArrowLeft, Mail, Lock, ShieldCheck } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, verifyTwoFactor } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  // Set once the password checks out on a 2FA account
  const [pendingToken, setPendingToken] = useState(null);
  const [code, setCode] = useState("");
  // "app" | "email" | "recovery"
  const [method, setMethod] = useState("app");
  const [emailSentTo, setEmailSentTo] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  // Show error toast if redirected from failed auth
  useEffect(() => {
    const error = searchParams.get("error");
    if (error === "rate_limited") {
      toast.error("Too many login attempts. Please wait a moment and try again.");
    } else if (error === "no_session") {
      toast.error("Session expired. Please try logging in again.");
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await login(email, password);
      if (data.two_factor_required) {
        setPendingToken(data.pending_token);
        return;
      }
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleCodeSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = await verifyTwoFactor(pendingToken, code);
      if (method === "recovery") {
        toast.success(`Welcome back! You have ${data.recovery_codes_left} recovery codes left.`);
      } else {
        toast.success("Welcome back!");
      }
      navigate("/dashboard");
    } catch (error) {
      if (error.status === 401) {
        // Pending token expired — start over from the password
        setPendingToken(null);
        setCode("");
      }
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const switchMethod = (next) => {
    setMethod(next);
    setCode("");
  };

  const sendEmailCode = async () => {
    setSendingEmail(true);
    try {
      const res = await fetch(`${API}/auth/2fa/email-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pending_token: pendingToken }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 401) {
        setPendingToken(null);
        setCode("");
      }
      if (!res.ok) {
        toast.error(data.detail || "We couldn't send the email. Please try again.");
        return;
      }
      setEmailSentTo(data.sent_to);
      switchMethod("email");
      toast.success(`Code sent to ${data.sent_to}`);
    } catch {
      toast.error("Network error. Please check your connection and try again.");
    } finally {
      setSendingEmail(false);
    }
  };

  const backToPassword = () => {
    setPendingToken(null);
    setCode("");
    setMethod("app");
    setEmailSentTo("");
    setPassword("");
  };

  const linkClass = "text-white/50 hover:text-white transition-colors disabled:opacity-50";

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex">
      <SEO
        title="Log In"
        description="Log in to your GottaSecond account. Access your dashboard, manage jobs, and connect with second shooters and studios."
        path="/login"
        noindex={true}
      />
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 md:px-12 lg:px-24 py-12">
        <Link to="/" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-12" data-testid="back-to-home">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm uppercase tracking-wider">Back to Home</span>
        </Link>

        <div className="max-w-md">
          {pendingToken ? (
            <>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3">
                One More Step
              </h1>
              <p className="text-white/50 text-lg mb-10">
                {method === "recovery" && "Enter one of the recovery codes you saved when you set up two-factor authentication."}
                {method === "email" && `We emailed a 6-digit code to ${emailSentTo}. It expires in 10 minutes.`}
                {method === "app" && "Enter the 6-digit code from your authenticator app."}
              </p>

              <form onSubmit={handleCodeSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="code" className="text-sm text-white/60 uppercase tracking-wider">
                    {method === "recovery" ? "Recovery code" : "Verification code"}
                  </Label>
                  <div className="relative">
                    <ShieldCheck className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                    <Input
                      id="code"
                      key={method}
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      placeholder={method === "recovery" ? "xxxxx-xxxxx" : "123456"}
                      inputMode={method === "recovery" ? "text" : "numeric"}
                      autoComplete="one-time-code"
                      autoFocus
                      maxLength={method === "recovery" ? 11 : 6}
                      className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white pl-12 h-12 rounded-sm tracking-widest"
                      required
                      data-testid="two-factor-code-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-white text-black hover:bg-gray-200 rounded-sm font-bold py-3 uppercase tracking-wide"
                  data-testid="two-factor-submit-btn"
                >
                  {loading ? "Verifying..." : "Verify"}
                </Button>
              </form>

              <div className="flex flex-col items-center gap-3 mt-6 text-sm">
                {method === "email" ? (
                  <button type="button" onClick={sendEmailCode} disabled={sendingEmail} className={linkClass} data-testid="resend-email-code">
                    {sendingEmail ? "Sending..." : "Didn't get it? Send a new code"}
                  </button>
                ) : (
                  <button type="button" onClick={sendEmailCode} disabled={sendingEmail} className={linkClass} data-testid="send-email-code">
                    {sendingEmail ? "Sending..." : "Can't use your app? Email me a code"}
                  </button>
                )}
                {method !== "app" && (
                  <button type="button" onClick={() => switchMethod("app")} className={linkClass}>
                    Use my authenticator app instead
                  </button>
                )}
                {method !== "recovery" && (
                  <button type="button" onClick={() => switchMethod("recovery")} className={linkClass} data-testid="toggle-recovery-code">
                    Use a recovery code
                  </button>
                )}
                <button type="button" onClick={backToPassword} className={linkClass}>
                  Back to sign in
                </button>
              </div>
            </>
          ) : (
          <>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3">
            Welcome Back
          </h1>
          <p className="text-white/50 text-lg mb-10">
            Sign in to access your account
          </p>

          {/* Email Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm text-white/60 uppercase tracking-wider">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white pl-12 h-12 rounded-sm"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm text-white/60 uppercase tracking-wider">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white pl-12 h-12 rounded-sm"
                  required
                  data-testid="password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-black hover:bg-gray-200 rounded-sm font-bold py-3 uppercase tracking-wide"
              data-testid="login-submit-btn"
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>

          <div className="text-center mt-4">
            <Link to="/forgot-password" className="text-white/50 hover:text-white text-sm transition-colors" data-testid="forgot-password-link">
              Forgot your password?
            </Link>
          </div>

          <p className="text-center text-white/50 mt-8">
            Don't have an account?{" "}
            <Link to="/register" className="text-white hover:underline" data-testid="register-link">
              Create one
            </Link>
          </p>
          </>
          )}
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1493599398321-988db97c9591?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxjaW5lbWElMjBjYW1lcmElMjBsZW5zJTIwZ2VhciUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzcwMTk1NDM5fDA&ixlib=rb-4.1.0&q=85"
          alt="Camera gear"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] to-transparent" />
      </div>
    </div>
  );
}
